import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  LinearProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import NoteIcon from '@mui/icons-material/Note';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import GestureIcon from '@mui/icons-material/Gesture';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { getDisplayStoreNumberByInternal } from '../../utils/storeNumber';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  TasksClient,
  TaskDetailDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
  TaskStatusesClient,
  TaskStatusDto,
  TaskStepDto,
  TaskCategoriesClient,
  TaskCategoryDto,
} from '../../app/services/api/tasksClient';

const tasksClient = TasksClient;
const prioritiesClient = TaskPrioritiesClient;
const statusesClient = TaskStatusesClient;
const categoriesClient = TaskCategoriesClient;

const TaskDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { stores, hasPermissionTo } = useContext(UserContext);

  // Permissions - using existing Role enum values
  const canView = hasPermissionTo ? hasPermissionTo(['AdvancedTasksView']) : false;
  const canViewEvidence = hasPermissionTo ? hasPermissionTo(['AdvancedTasksViewEvidence']) : false;
  const canViewHistory = hasPermissionTo ? hasPermissionTo(['AdvancedTasksViewAudit']) : false;
  const canUpdateProgress = hasPermissionTo ? hasPermissionTo(['AdvancedTasksCreate']) : false;
  const canChangePriority = hasPermissionTo ? hasPermissionTo(['AdvancedTasksRePrioritise']) : false;
  // Using AdvancedTasksReassign for approval permissions as it's the closest existing permission
  const canApproveReject = hasPermissionTo ? hasPermissionTo(['AdvancedTasksReassign']) : false;

  const [task, setTask] = useState<TaskDetailDto | null>(null);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);
  const [statuses, setStatuses] = useState<TaskStatusDto[]>([]);
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Change priority dialog
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [newPriorityId, setNewPriorityId] = useState<number | ''>('');

  // Reassign dialog
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');

  // Extend due time dialog
  const [extendDueDialogOpen, setExtendDueDialogOpen] = useState(false);
  const [newDueDate, setNewDueDate] = useState<string>('');
  const [newDueTime, setNewDueTime] = useState<string>('');
  const [extendReason, setExtendReason] = useState<string>('');

  // Approval dialog
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalDecision, setApprovalDecision] = useState<'Compliant' | 'NonCompliant' | ''>('');
  const [approvalNotes, setApprovalNotes] = useState<string>('');
  const [sendBackForRework, setSendBackForRework] = useState(false);

  // Step view dialog (for viewing step content before completing)
  const [stepViewDialogOpen, setStepViewDialogOpen] = useState(false);
  const [viewingStep, setViewingStep] = useState<TaskStepDto | null>(null);

  // Mock users and roles for reassignment
  const availableUsers = [
    { id: 1001, name: 'Alex Smith' },
    { id: 1002, name: 'Jordan Lee' },
    { id: 1003, name: 'Taylor Brown' },
    { id: 1004, name: 'Morgan Davis' },
    { id: 1005, name: 'Casey Wilson' },
  ];

  const availableRoles = [
    { id: 1, name: 'Store Manager' },
    { id: 2, name: 'Assistant Manager' },
    { id: 3, name: 'Team Lead' },
    { id: 4, name: 'Sales Associate' },
    { id: 5, name: 'Stock Room' },
  ];

  // Step completion modal
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<TaskStepDto | null>(null);
  const [stepComment, setStepComment] = useState('');
  const [stepEvidenceType, setStepEvidenceType] = useState<string>('text');
  const [stepPhotoFile, setStepPhotoFile] = useState<File | null>(null);
  const [stepPhotoPreview, setStepPhotoPreview] = useState<string | null>(null);
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);
  const [capturedLocation, setCapturedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Signature canvas ref and state
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Step completion tracking (local state for demo)
  const [completedStepIds, setCompletedStepIds] = useState<number[]>([]);

  // Pagination state for steps
  const [stepsPage, setStepsPage] = useState(0);
  const [stepsRowsPerPage, setStepsRowsPerPage] = useState(5);

  // Pagination state for history
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(5);

  // Pagination state for evidence
  const [evidencePage, setEvidencePage] = useState(0);
  const [evidenceRowsPerPage, setEvidenceRowsPerPage] = useState(5);

  // Track if task was sent back for rework (should return to pending approval when steps recompleted)
  const [sentBackForRework, setSentBackForRework] = useState(false);

  useEffect(() => {
    if (!canView || !id) return;

    setLoading(true);
    Promise.all([
      tasksClient.getTask(Number(id)),
      prioritiesClient.getPriorities(),
      statusesClient.getStatuses(),
      categoriesClient.getCategories(),
    ])
      .then(([taskData, priorityData, statusData, categoryData]) => {
        setTask(taskData);
        setPriorities(priorityData);
        setStatuses(statusData);
        setCategories(categoryData);
        // Initialize completed steps based on percentage (demo logic)
        if (taskData.steps.length > 0) {
          const completedCount = Math.floor(
            (taskData.percentageComplete / 100) * taskData.steps.length,
          );
          setCompletedStepIds(taskData.steps.slice(0, completedCount).map(s => s.id));
        }
        // If task is in Pending Approval status, track that it needs approval after any rework
        // This handles cases where task.requiresApproval might be false but task is already in approval status
        if (taskData.statusId === 3) {
          setSentBackForRework(true);
        }
      })
      .catch(() => {
        setTask(null);
      })
      .finally(() => setLoading(false));
  }, [canView, id]);

  const getPriorityName = (priorityId: number) =>
    priorities.find(p => p.id === priorityId)?.name ?? 'Unknown';

  const getStatusName = (statusId: number) =>
    statuses.find(s => s.id === statusId)?.name ?? 'Unknown';

  const getCategoryName = (categoryId?: number | null) =>
    categoryId ? categories.find(c => c.id === categoryId)?.name : null;

  const getPriorityColor = (priorityId: number): 'default' | 'error' | 'warning' | 'success' => {
    const name = getPriorityName(priorityId).toLowerCase();
    if (name === 'high') return 'error';
    if (name === 'medium') return 'warning';
    return 'default';
  };

  const getStatusColor = (statusId: number): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary' => {
    // 1=Open, 2=In Progress, 3=Pending Approval, 4=Approved, 5=Rejected, 6=Completed
    switch (statusId) {
      case 6: return 'success';    // Completed
      case 4: return 'success';    // Approved
      case 5: return 'error';      // Rejected
      case 3: return 'warning';    // Pending Approval
      case 2: return 'info';       // In Progress
      default: return 'secondary'; // Open (1) or unknown
    }
  };

  const getProgressBarColor = (statusId: number): 'inherit' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary' => {
    // 1=Open, 2=In Progress, 3=Pending Approval, 4=Approved, 5=Rejected, 6=Completed
    switch (statusId) {
      case 6: return 'success';    // Completed
      case 4: return 'success';    // Approved
      case 5: return 'error';      // Rejected
      case 3: return 'warning';    // Pending Approval
      case 2: return 'info';       // In Progress
      default: return 'primary';   // Open (1) or unknown - use primary for progress bar
    }
  };

  const isTaskIncomplete = task && task.statusId !== 6; // statusId 6 = Completed

  // Get available priorities for change (default or higher)
  const getAvailablePriorities = () => {
    if (!task) return [];
    // Allow changing to same or higher priority (Low=1, Medium=2, High=3)
    return priorities.filter(p => p.id >= task.defaultPriorityId);
  };

  // Determine if task has specific restrictions
  const hasUserRestriction = task?.targetUserId != null;
  const hasRoleRestriction = task?.targetRoleId != null;
  const hasRestriction = hasUserRestriction || hasRoleRestriction;

  const handleBack = () => {
    navigate('/tasks');
  };

  // Step completion modal handlers
  const handleOpenStepModal = (step: TaskStepDto) => {
    if (!canUpdateProgress || !isTaskIncomplete) return;
    if (completedStepIds.includes(step.id)) return; // Already completed

    setSelectedStep(step);
    setStepComment('');
    setStepEvidenceType(step.evidenceType || 'text');
    setStepPhotoFile(null);
    setStepPhotoPreview(null);
    setCapturedLocation(null);
    setLocationError(null);
    setHasSignature(false);
    setStepModalOpen(true);
  };

  // Photo file handling
  const handlePhotoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setStepPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setStepPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearPhotoFile = () => {
    setStepPhotoFile(null);
    setStepPhotoPreview(null);
  };

  // Signature canvas handlers
  const initSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const handleSignatureMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleSignatureMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
    setHasSignature(true);
  };

  const handleSignatureMouseUp = () => {
    setIsDrawing(false);
  };

  const handleSignatureTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    ctx.beginPath();
    ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
  };

  const handleSignatureTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
    ctx.stroke();
    setHasSignature(true);
  };

  const clearSignature = () => {
    initSignatureCanvas();
    setHasSignature(false);
  };

  const getSignatureDataUrl = (): string | null => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !hasSignature) return null;
    return canvas.toDataURL('image/png');
  };

  // Location capture
  const captureLocation = (): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser');
        resolve(null);
        return;
      }

      setIsCapturingLocation(true);
      setLocationError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCapturedLocation(location);
          setIsCapturingLocation(false);
          resolve(location);
        },
        (error) => {
          let errorMessage = 'Unable to retrieve your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          setLocationError(errorMessage);
          setIsCapturingLocation(false);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  };

  const handleConfirmStepComplete = async () => {
    if (!selectedStep || !task) return;

    // If evidence is required, check we have what's needed
    if (selectedStep.evidenceRequired) {
      if (stepEvidenceType === 'photo' && !stepPhotoFile) {
        return;
      }
      if (stepEvidenceType === 'signature' && !hasSignature) {
        return;
      }
      if (stepEvidenceType === 'text' && !stepComment.trim()) {
        return;
      }
    }

    // Capture location
    const location = await captureLocation();

    const now = new Date().toISOString();
    const newCompletedStepIds = [...completedStepIds, selectedStep.id];
    const totalSteps = task.steps.length;
    const completedCount = newCompletedStepIds.length;
    const newPercentage = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

    // Determine if this is the first step being completed (task starts)
    const isFirstStep = completedStepIds.length === 0;
    // Determine if all steps are now complete
    const isLastStep = completedCount === totalSteps;

    // Prepare history events
    const historyEvents: Array<{
      id: number;
      eventType: string;
      oldValue: string | null;
      newValue: string | null;
      comment: string | null;
      createdAtUtc: string;
      createdByUserId: number | null;
    }> = [];

    // Create step completed event
    historyEvents.push({
      id: Date.now(),
      eventType: 'StepCompleted',
      oldValue: null,
      newValue: `Step ${selectedStep.order}: ${selectedStep.instruction}`,
      comment: stepComment || null,
      createdAtUtc: now,
      createdByUserId: 1001, // Demo user
    });

    // If this is the first step, create a "Task Started" event and update status
    let newStatusId = task.statusId;
    let newStartDate = task.startDateUtc;
    let newCompletedDate = task.completedDateUtc;

    if (isFirstStep && task.statusId === 1) { // statusId 1 = Open/Not Started
      newStatusId = 2; // In Progress
      newStartDate = now;
      historyEvents.push({
        id: Date.now() + 1,
        eventType: 'StatusChanged',
        oldValue: getStatusName(task.statusId),
        newValue: getStatusName(2),
        comment: 'Task started - first step completed',
        createdAtUtc: now,
        createdByUserId: 1001,
      });
    }

    // If all steps are complete, check if approval is required
    if (isLastStep) {
      // Task requires approval if: requiresApproval flag is true OR task was sent back for rework
      const needsApproval = task.requiresApproval || sentBackForRework;

      if (needsApproval) {
        // Task requires approval - set to Pending Approval status
        newStatusId = 3; // Pending Approval
        // Reset the sentBackForRework flag since we're now back in pending approval
        setSentBackForRework(false);
        historyEvents.push({
          id: Date.now() + 2,
          eventType: 'StatusChanged',
          oldValue: getStatusName(task.statusId),
          newValue: getStatusName(3),
          comment: 'All steps completed - awaiting approval',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
      } else {
        // No approval required - mark as completed
        newStatusId = 6; // Completed
        newCompletedDate = now;
        historyEvents.push({
          id: Date.now() + 2,
          eventType: 'StatusChanged',
          oldValue: getStatusName(task.statusId),
          newValue: getStatusName(6),
          comment: 'All steps completed',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
      }
    }

    // Add to completed steps
    setCompletedStepIds(newCompletedStepIds);

    // Prepare evidence data
    let evidenceText = stepComment;
    let fileUrl: string | null = null;

    if (stepEvidenceType === 'signature') {
      fileUrl = getSignatureDataUrl();
      evidenceText = stepComment || 'Signature captured';
    } else if (stepEvidenceType === 'photo' && stepPhotoFile) {
      fileUrl = stepPhotoPreview; // In real impl, upload to server
      evidenceText = stepComment || `Photo: ${stepPhotoFile.name}`;
    }

    // Build new evidence array
    const newEvidenceArray = [...task.evidence];
    if (evidenceText || fileUrl) {
      newEvidenceArray.push({
        id: Date.now() + 10,
        evidenceType: stepEvidenceType,
        evidenceText: evidenceText || null,
        fileUrl: fileUrl,
        latitude: location?.lat ?? null,
        longitude: location?.lng ?? null,
        capturedAtUtc: now,
        capturedByUserId: 1001, // Demo user
      });
    }

    // Update task with all changes
    setTask(prev => prev ? {
      ...prev,
      statusId: newStatusId,
      startDateUtc: newStartDate,
      completedDateUtc: newCompletedDate,
      percentageComplete: newPercentage,
      evidence: newEvidenceArray,
      history: [...prev.history, ...historyEvents],
    } : null);

    // Reset modal state
    setStepModalOpen(false);
    setSelectedStep(null);
    setStepComment('');
    setStepPhotoFile(null);
    setStepPhotoPreview(null);
    setHasSignature(false);
    setCapturedLocation(null);
  };

  const handleCancelStepModal = () => {
    setStepModalOpen(false);
    setSelectedStep(null);
    setStepComment('');
    setStepPhotoFile(null);
    setStepPhotoPreview(null);
    setHasSignature(false);
    setCapturedLocation(null);
    setLocationError(null);
  };

  // Check if step can be completed
  const canCompleteStep = () => {
    if (!selectedStep) return false;
    if (!selectedStep.evidenceRequired) return true; // Can always complete if not required

    switch (stepEvidenceType) {
      case 'photo':
        return !!stepPhotoFile;
      case 'signature':
        return hasSignature;
      case 'text':
        return !!stepComment.trim();
      default:
        return true;
    }
  };

  // Priority change handlers
  const handleOpenPriorityDialog = () => {
    if (!task) return;
    setNewPriorityId(task.priorityId);
    setPriorityDialogOpen(true);
  };

  const handleConfirmPriorityChange = () => {
    if (!task || !newPriorityId) return;

    // In real implementation, call API to update priority
    setTask(prev =>
      prev
        ? {
          ...prev,
          priorityId: Number(newPriorityId),
        }
        : null,
    );
    setPriorityDialogOpen(false);
  };

  const handleCancelPriorityDialog = () => {
    setPriorityDialogOpen(false);
  };

  // Reassign handlers
  const handleOpenReassignDialog = () => {
    if (!task) return;
    // Pre-select current assignment
    if (task.targetUserId) {
      setSelectedUserId(task.targetUserId);
      setSelectedRoleId('');
    } else if (task.targetRoleId) {
      setSelectedRoleId(task.targetRoleId);
      setSelectedUserId('');
    } else {
      setSelectedUserId('');
      setSelectedRoleId('');
    }
    setReassignDialogOpen(true);
  };

  const handleConfirmReassign = () => {
    if (!task) return;

    const now = new Date().toISOString();
    let newTargetUserId: number | null = null;
    let newTargetUserName: string | null = null;
    let newTargetRoleId: number | null = null;
    let newTargetRoleName: string | null = null;

    if (selectedUserId) {
      const user = availableUsers.find(u => u.id === selectedUserId);
      newTargetUserId = Number(selectedUserId);
      newTargetUserName = user?.name ?? null;
    } else if (selectedRoleId) {
      const role = availableRoles.find(r => r.id === selectedRoleId);
      newTargetRoleId = Number(selectedRoleId);
      newTargetRoleName = role?.name ?? null;
    }

    // Build history event
    const oldAssignment = task.targetUserName
      ? `User: ${task.targetUserName}`
      : task.targetRoleName
        ? `Role: ${task.targetRoleName}`
        : 'Unassigned';
    const newAssignment = newTargetUserName
      ? `User: ${newTargetUserName}`
      : newTargetRoleName
        ? `Role: ${newTargetRoleName}`
        : 'Unassigned';

    const historyEvent = {
      id: Date.now(),
      eventType: 'Reassigned',
      oldValue: oldAssignment,
      newValue: newAssignment,
      comment: null,
      createdAtUtc: now,
      createdByUserId: 1001, // Demo user
    };

    setTask(prev => prev ? {
      ...prev,
      targetUserId: newTargetUserId,
      targetUserName: newTargetUserName,
      targetRoleId: newTargetRoleId,
      targetRoleName: newTargetRoleName,
      history: [...prev.history, historyEvent],
    } : null);

    setReassignDialogOpen(false);
  };

  const handleCancelReassignDialog = () => {
    setReassignDialogOpen(false);
  };

  // Extend due time handlers
  const handleOpenExtendDueDialog = () => {
    if (!task || !task.dueDateUtc) return;
    const currentDue = new Date(task.dueDateUtc);
    setNewDueDate(currentDue.toISOString().split('T')[0]);
    setNewDueTime(currentDue.toTimeString().slice(0, 5));
    setExtendReason('');
    setExtendDueDialogOpen(true);
  };

  const handleConfirmExtendDue = () => {
    if (!task || !newDueDate || !newDueTime) return;

    const newDueDateTimeUtc = new Date(`${newDueDate}T${newDueTime}:00`).toISOString();
    const oldDueDateUtc = task.dueDateUtc;

    // Create audit history event
    const historyEvent = {
      id: Date.now(),
      eventType: 'DueTimeExtended',
      oldValue: oldDueDateUtc ?? null,
      newValue: JSON.stringify({ newDueDateTimeUtc, reason: extendReason }),
      comment: extendReason || null,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1001, // Current user ID
    };

    // Update task with new due date and history event
    setTask(prev => prev ? {
      ...prev,
      dueDateUtc: newDueDateTimeUtc,
      history: [...prev.history, historyEvent],
    } : null);

    setExtendDueDialogOpen(false);
  };

  const handleCancelExtendDueDialog = () => {
    setExtendDueDialogOpen(false);
  };

  // Approval workflow handlers
  const handleOpenApprovalDialog = () => {
    if (!task) return;
    setApprovalDecision('');
    setApprovalNotes('');
    setSendBackForRework(false);
    setApprovalDialogOpen(true);
  };

  const handleConfirmApproval = () => {
    if (!task || !approvalDecision) return;

    const now = new Date().toISOString();
    const historyEvents: Array<{
      id: number;
      eventType: string;
      oldValue: string | null;
      newValue: string | null;
      comment: string | null;
      createdAtUtc: string;
      createdByUserId: number | null;
    }> = [];

    let newStatusId = task.statusId;
    let newCompletedDate = task.completedDateUtc;

    if (approvalDecision === 'Compliant') {
      // Approve - mark as completed (status 6)
      newStatusId = 6; // Completed
      newCompletedDate = now;
      historyEvents.push({
        id: Date.now(),
        eventType: 'TaskApproved',
        oldValue: getStatusName(task.statusId),
        newValue: 'Compliant',
        comment: approvalNotes || 'Task approved',
        createdAtUtc: now,
        createdByUserId: 1001,
      });
      historyEvents.push({
        id: Date.now() + 1,
        eventType: 'StatusChanged',
        oldValue: getStatusName(task.statusId),
        newValue: getStatusName(6),
        comment: 'Task approved and completed',
        createdAtUtc: now,
        createdByUserId: 1001,
      });
    } else if (approvalDecision === 'NonCompliant') {
      if (sendBackForRework) {
        // Reject and send back - clear steps, set status to In Progress (status 2)
        newStatusId = 2; // In Progress
        setCompletedStepIds([]); // Clear all completed steps
        historyEvents.push({
          id: Date.now(),
          eventType: 'TaskRejected',
          oldValue: getStatusName(task.statusId),
          newValue: 'NonCompliant - Sent Back for Rework',
          comment: approvalNotes || 'Task rejected and sent back for rework',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
        historyEvents.push({
          id: Date.now() + 1,
          eventType: 'StepsCleared',
          oldValue: `${completedStepIds.length} steps completed`,
          newValue: '0 steps completed',
          comment: 'All steps cleared for rework',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
        historyEvents.push({
          id: Date.now() + 2,
          eventType: 'StatusChanged',
          oldValue: getStatusName(task.statusId),
          newValue: getStatusName(2),
          comment: 'Task sent back for rework',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
      } else {
        // Reject but keep as is (status 5 = Rejected)
        newStatusId = 5; // Rejected
        historyEvents.push({
          id: Date.now(),
          eventType: 'TaskRejected',
          oldValue: getStatusName(task.statusId),
          newValue: 'NonCompliant',
          comment: approvalNotes || 'Task rejected',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
        historyEvents.push({
          id: Date.now() + 1,
          eventType: 'StatusChanged',
          oldValue: getStatusName(task.statusId),
          newValue: getStatusName(5),
          comment: 'Task rejected',
          createdAtUtc: now,
          createdByUserId: 1001,
        });
      }
    }

    setTask(prev => prev ? {
      ...prev,
      statusId: newStatusId,
      completedDateUtc: newCompletedDate,
      percentageComplete: sendBackForRework ? 0 : prev.percentageComplete,
      approvalResult: approvalDecision as 'Compliant' | 'NonCompliant',
      approvalNotes: approvalNotes || null,
      approvalDateUtc: now,
      approvalByUserId: 1001,
      history: [...prev.history, ...historyEvents],
    } : null);

    setApprovalDialogOpen(false);
  };

  const handleCancelApprovalDialog = () => {
    setApprovalDialogOpen(false);
  };

  // Clear steps handler (for sending back a task for rework without going through full approval)
  const handleClearSteps = () => {
    if (!task) return;

    const now = new Date().toISOString();
    const wasInPendingApproval = task.statusId === 3;

    const historyEvent = {
      id: Date.now(),
      eventType: 'StepsCleared',
      oldValue: `${completedStepIds.length} steps completed`,
      newValue: '0 steps completed',
      comment: wasInPendingApproval ? 'Steps cleared - sent back for rework' : 'Steps cleared manually',
      createdAtUtc: now,
      createdByUserId: 1001,
    };

    // If task was in pending approval, mark it as sent back for rework
    if (wasInPendingApproval) {
      setSentBackForRework(true);
    }

    setCompletedStepIds([]);
    setTask(prev => prev ? {
      ...prev,
      statusId: prev.statusId === 3 ? 2 : prev.statusId, // If pending approval, move back to in progress
      percentageComplete: 0,
      history: [...prev.history, historyEvent],
    } : null);
  };

  // Step view dialog handlers (for viewing step content before completing)
  const handleOpenStepViewDialog = (step: TaskStepDto) => {
    setViewingStep(step);
    setStepViewDialogOpen(true);
  };

  const handleCloseStepViewDialog = () => {
    setStepViewDialogOpen(false);
    setViewingStep(null);
  };

  const handleViewAndComplete = () => {
    if (viewingStep) {
      setStepViewDialogOpen(false);
      handleOpenStepModal(viewingStep);
      setViewingStep(null);
    }
  };

  // Get step type icon
  const getStepTypeIcon = (stepType: string | undefined) => {
    switch (stepType) {
      case 'document':
        return <DescriptionIcon fontSize="small" />;
      case 'url':
        return <LinkIcon fontSize="small" />;
      case 'text':
      default:
        return <TextFieldsIcon fontSize="small" />;
    }
  };

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const getEvidenceIcon = (evidenceType: string) => {
    switch (evidenceType.toLowerCase()) {
      case 'photo':
        return <PhotoCameraIcon fontSize="small" />;
      case 'note':
        return <NoteIcon fontSize="small" />;
      case 'location':
        return <LocationOnIcon fontSize="small" />;
      default:
        return <NoteIcon fontSize="small" />;
    }
  };

  // Determine what to show for assignment target
  const getAssignmentDisplay = () => {
    if (!task) return null;
    if (task.targetUserName) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" />
          <span>{task.targetUserName}</span>
        </Box>
      );
    }
    if (task.targetRoleName) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon fontSize="small" />
          <span>{task.targetRoleName}</span>
        </Box>
      );
    }
    return task.storeName || getDisplayStoreNumberByInternal(stores, task.storeNumber);
  };

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>Task Details</PageTitle>
        <Pill
          text="You do not have permission to view task details."
          pallet={theme.palette.error}
        />
      </PageArea>
    );
  }

  if (loading) {
    return (
      <PageArea>
        <PageTitle>Task Details</PageTitle>
        <LinearProgress />
      </PageArea>
    );
  }

  if (!task) {
    return (
      <PageArea>
        <PageTitle>Task Details</PageTitle>
        <Pill
          text="Task not found."
          pallet={theme.palette.error}
        />
        <Button
          variant="secondary"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Tasks
        </Button>
      </PageArea>
    );
  }

  const stepsProgress =
    task.steps.length > 0
      ? Math.round((completedStepIds.length / task.steps.length) * 100)
      : task.percentageComplete;

  return (
    <PageArea>
      <Grid container
        spacing={3}>
        {/* Header */}
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              aria-label="Back to tasks"
              onClick={handleBack}
            >
              <ArrowBackIcon />
            </IconButton>
            <PageTitle>Task Details</PageTitle>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canChangePriority && isTaskIncomplete && (
              <Button
                variant="secondary"
                startIcon={<PriorityHighIcon />}
                onClick={handleOpenPriorityDialog}
              >
                Change Priority
              </Button>
            )}
            {isTaskIncomplete && (
              <Button
                variant="secondary"
                startIcon={<SwapHorizIcon />}
                onClick={handleOpenReassignDialog}
              >
                Reassign
              </Button>
            )}
          </Box>
        </Grid>

        {/* Task Summary */}
        <Grid item
          xs={12}>
          <Section style={{ padding: 8 }}>
            {/* Header row with Title + Priority + Category on left, Template link on right */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <h3 style={{ margin: 0 }}>{task.title}</h3>
                <Chip
                  label={getPriorityName(task.priorityId)}
                  color={getPriorityColor(task.priorityId)}
                  size="small"
                />
                {getCategoryName(task.categoryId) && (
                  <Chip
                    label={getCategoryName(task.categoryId)}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              <div>
                {task.templateId ? (
                  <Link
                    to={`/task-templates/${task.templateId}`}
                    style={{ color: '#1976d2', textDecoration: 'none' }}
                  >
                    {'View Template'}
                  </Link>
                ) : (
                  <span style={{ color: '#666' }}>Ad-hoc Task</span>
                )}
              </div>
            </Box>

            <Grid container
              spacing={3}>
              {/* Description - full width */}
              <Grid item
                xs={12}
                md={6}>
                <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: 4 }}>
                  Description
                </div>
                <div>{task.description || '—'}</div>
              </Grid>

              {/* Store and Assigned To side by side */}
              <Grid item
                xs={6}
                md={3}>
                <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: 4 }}>
                  Store
                </div>
                <div>{task.storeName || getDisplayStoreNumberByInternal(stores, task.storeNumber)}</div>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: 4 }}>
                  Assigned To
                </div>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ...(hasRestriction && {
                      backgroundColor: '#fff3e0',
                      border: '1px solid #ffb74d',
                      borderRadius: 1,
                      padding: '4px 8px',
                    }),
                  }}
                >
                  {getAssignmentDisplay()}
                  {hasRestriction && (
                    <Chip
                      label={hasUserRestriction ? 'User Only' : 'Role Only'}
                      size="small"
                      color="warning"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </Grid>

              {/* Progress section - full width with status chip as label */}
              <Grid item
                xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: '-16px', width: '100%' }}>
                  <Chip
                    label={getStatusName(task.statusId)}
                    color={getStatusColor(task.statusId)}
                    size="small"
                    style={{ zIndex: 1 }}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stepsProgress}
                  color={getProgressBarColor(task.statusId)}
                  sx={{ width: '100%', height: 8, borderRadius: 4, mb: 1 }}
                />
                {/* Dates spread evenly underneath */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <div>
                    <div style={{ color: '#666', fontSize: '0.75rem' }}>Started</div>
                    <div style={{ fontSize: '0.875rem' }}>{formatDateTime(task.startDateUtc)}</div>
                  </div>
                  {!task.completedDateUtc ?
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#666', fontSize: '0.75rem' }}>Due Date</div>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <div style={{ fontSize: '0.875rem' }}>{formatDateTime(task.dueDateUtc)}</div>
                        {isTaskIncomplete && task.dueDateUtc && (
                          <IconButton
                            aria-label="Extend due time"
                            size="small"
                            onClick={handleOpenExtendDueDialog}
                            title="Extend Due Time"
                            sx={{ p: 0.25 }}
                          >
                            <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        )}
                      </Box>
                    </div> :
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#666', fontSize: '0.75rem' }}>Completed</div>
                      <div style={{ fontSize: '0.875rem' }}>{formatDateTime(task.completedDateUtc)}</div>
                    </div>
                  }
                </Box>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Approval Section - Show for tasks pending approval */}
        {task.statusId === 3 && canApproveReject && (
          <Grid
            item
            xs={12}
          >
            <Section style={{ p: 8, backgroundColor: '#fff8e1', border: '1px solid #ffb74d' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <h4 style={{ margin: 0, marginBottom: 8 }}>Pending Approval</h4>
                  <p style={{ margin: 0, color: '#666' }}>
                    This task requires approval. All steps have been completed and the task is awaiting review.
                  </p>
                  {task.approverUserName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <PersonIcon fontSize="small" />
                      <span>Assigned Approver: {task.approverUserName}</span>
                    </Box>
                  )}
                  {task.approverPersonaName && !task.approverUserName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <GroupIcon fontSize="small" />
                      <span>Assigned Approver Role: {task.approverPersonaName}</span>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="primary"
                    size="md"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleOpenApprovalDialog}
                  >
                    Review & Approve
                  </Button>
                </Box>
              </Box>
            </Section>
          </Grid>
        )}

        {/* Steps Section */}
        {task.steps.length > 0 && (
          <Grid item
            xs={12}>
            <Section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TableHeader>Steps</TableHeader>
                {/* Clear Steps button for tasks pending approval or in progress */}
                {canApproveReject && (task.statusId === 3 || task.statusId === 2) && completedStepIds.length > 0 && (
                  <Button
                    variant="secondary"
                    size="md"
                    style={{ marginRight: 16 }}
                    startIcon={<RefreshIcon />}
                    onClick={handleClearSteps}
                  >
                    Clear Steps
                  </Button>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>#</TableCell>
                      <TableCell width={80}>Type</TableCell>
                      <TableCell>Instruction</TableCell>
                      <TableCell width={120}>Evidence Required</TableCell>
                      <TableCell width={100}>Status</TableCell>
                      <TableCell width={80}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {task.steps
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .slice(stepsPage * stepsRowsPerPage, stepsPage * stepsRowsPerPage + stepsRowsPerPage)
                      .map(step => {
                        const isCompleted = completedStepIds.includes(step.id);
                        const hasContent = step.stepType === 'document' || step.stepType === 'url' || (step.stepType === 'text' && step.instruction);
                        return (
                          <TableRow
                            key={step.id}
                            hover
                            sx={{
                              opacity: isCompleted ? 0.7 : 1,
                            }}
                          >
                            <TableCell>{step.order}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getStepTypeIcon(step.stepType)}
                                <span style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                                  {step.stepType || 'text'}
                                </span>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textDecoration: isCompleted ? 'line-through' : 'none' }}>
                              {step.instruction}
                              {step.stepType === 'document' && step.linkedDocumentName && (
                                <Box sx={{ fontSize: '0.75rem', color: '#1976d2', mt: 0.5 }}>
                                  📄 {step.linkedDocumentName}
                                </Box>
                              )}
                              {step.stepType === 'url' && step.linkedUrl && (
                                <Box sx={{ fontSize: '0.75rem', color: '#1976d2', mt: 0.5 }}>
                                  🔗 {step.linkedUrlLabel || step.linkedUrl}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {step.evidenceRequired ? (
                                <Chip
                                  label={step.evidenceType || 'Required'}
                                  size="small"
                                  color="warning"
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              {isCompleted ? (
                                <Chip label="Done"
                                  size="small"
                                  color="success" />
                              ) : (
                                <Chip label="Pending"
                                  size="small"
                                  color="default" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {hasContent && (
                                  <IconButton
                                    aria-label="View step content"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenStepViewDialog(step);
                                    }}
                                    title="View Step Content"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {canUpdateProgress && isTaskIncomplete && !isCompleted && (
                                  <IconButton
                                    aria-label="Complete step"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenStepModal(step);
                                    }}
                                    title="Complete Step"
                                    color="primary"
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        count={task.steps.length}
                        rowsPerPage={stepsRowsPerPage}
                        page={stepsPage}
                        onPageChange={(_, newPage) => setStepsPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setStepsRowsPerPage(parseInt(e.target.value, 10));
                          setStepsPage(0);
                        }}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Section>
          </Grid>
        )}



        {/* Show approval result if task was approved/rejected */}
        {task.approvalResult && (
          <Grid
            item
            xs={12}
          >
            <Section style={{
              backgroundColor: task.approvalResult === 'Compliant' ? '#e8f5e9' : '#ffebee',
              border: `1px solid ${task.approvalResult === 'Compliant' ? '#4caf50' : '#f44336'}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {task.approvalResult === 'Compliant' ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <CancelIcon color="error" />
                )}
                <Box>
                  <h4 style={{ margin: 0, marginBottom: 4 }}>
                    {task.approvalResult === 'Compliant' ? 'Approved' : 'Rejected'}
                  </h4>
                  {task.approvalNotes && (
                    <p style={{ margin: 0, color: '#666' }}>{task.approvalNotes}</p>
                  )}
                  {task.approvalDateUtc && (
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
                      {formatDateTime(task.approvalDateUtc)} by User #{task.approvalByUserId}
                    </p>
                  )}
                </Box>
              </Box>
            </Section>
          </Grid>
        )}

        {/* Evidence Section - Always show table */}
        {canViewEvidence && (
          <Grid item
            xs={12}>
            <Section>
              <TableHeader>Evidence</TableHeader>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>Type</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell width={180}>Captured At</TableCell>
                      <TableCell width={100}>By User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {task.evidence.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 4, color: '#666' }}
                        >
                          No evidence has been captured for this task.
                        </TableCell>
                      </TableRow>
                    ) : (
                      task.evidence
                        .slice(evidencePage * evidenceRowsPerPage, evidencePage * evidenceRowsPerPage + evidenceRowsPerPage)
                        .map(ev => (
                          <TableRow key={ev.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getEvidenceIcon(ev.evidenceType)}
                                {ev.evidenceType}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {ev.evidenceText && <div>{ev.evidenceText}</div>}
                              {ev.fileUrl && (
                                <a
                                  href={ev.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#1976d2' }}
                                >
                                  View File
                                </a>
                              )}
                              {ev.latitude && ev.longitude && (
                                <div style={{ color: '#666', fontSize: '0.75rem' }}>
                                  📍 {ev.latitude.toFixed(4)}, {ev.longitude.toFixed(4)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>{formatDateTime(ev.capturedAtUtc)}</TableCell>
                            <TableCell>User #{ev.capturedByUserId}</TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                  {task.evidence.length > 0 && (
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          count={task.evidence.length}
                          rowsPerPage={evidenceRowsPerPage}
                          page={evidencePage}
                          onPageChange={(_, newPage) => setEvidencePage(newPage)}
                          onRowsPerPageChange={(e) => {
                            setEvidenceRowsPerPage(parseInt(e.target.value, 10));
                            setEvidencePage(0);
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </Section>
          </Grid>
        )}

        {/* History Section */}
        {canViewHistory && (
          <Grid item
            xs={12}>
            <Section>
              <TableHeader>Task History</TableHeader>
              {task.history.length === 0 ? (
                <p style={{ color: '#666', marginTop: 16 }}>
                  No history available for this task.
                </p>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={150}>Event</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell width={180}>Timestamp</TableCell>
                        <TableCell width={100}>By User</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {task.history
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime(),
                        )
                        .slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage)
                        .map(ev => (
                          <TableRow key={ev.id}>
                            <TableCell>
                              <Chip
                                label={ev.eventType}
                                size="small"
                                color={
                                  ev.eventType === 'Created'
                                    ? 'primary'
                                    : ev.eventType === 'StatusChanged'
                                      ? 'warning'
                                      : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {ev.oldValue && ev.newValue && (
                                <div>
                                  {ev.oldValue} → {ev.newValue}
                                </div>
                              )}
                              {ev.comment && (
                                <div style={{ color: '#666' }}>{ev.comment}</div>
                              )}
                              {!ev.oldValue && !ev.newValue && !ev.comment && '—'}
                            </TableCell>
                            <TableCell>{formatDateTime(ev.createdAtUtc)}</TableCell>
                            <TableCell>
                              {ev.createdByUserId ? `User #${ev.createdByUserId}` : 'System'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          count={task.history.length}
                          rowsPerPage={historyRowsPerPage}
                          page={historyPage}
                          onPageChange={(_, newPage) => setHistoryPage(newPage)}
                          onRowsPerPageChange={(e) => {
                            setHistoryRowsPerPage(parseInt(e.target.value, 10));
                            setHistoryPage(0);
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              )}
            </Section>
          </Grid>
        )}
      </Grid>

      {/* Step Completion Modal */}
      <Dialog open={stepModalOpen}
        onClose={handleCancelStepModal}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Complete Step</DialogTitle>
        <DialogContent>
          {selectedStep && (
            <>
              <p style={{ marginBottom: 16 }}>
                <strong>Step {selectedStep.order}:</strong> {selectedStep.instruction}
              </p>

              {selectedStep.evidenceRequired && (
                <p style={{ marginBottom: 8, color: '#d32f2f' }}>
                  * Evidence is required to complete this step
                </p>
              )}

              {/* Evidence Type Selector */}
              <TextField
                select
                label="Evidence Type"
                fullWidth
                size="small"
                value={stepEvidenceType}
                onChange={e => {
                  setStepEvidenceType(e.target.value);
                  // Clear previous evidence when changing type
                  setStepPhotoFile(null);
                  setStepPhotoPreview(null);
                  setHasSignature(false);
                }}
                sx={{ mb: 2 }}
              >
                <MenuItem value="text">Text Note</MenuItem>
                <MenuItem value="photo">Photo Upload</MenuItem>
                <MenuItem value="signature">Signature</MenuItem>
              </TextField>

              {/* Photo Upload Section */}
              {stepEvidenceType === 'photo' && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="secondary"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Upload Photo
                    <input
                      hidden
                      accept="image/*"
                      aria-label="Upload evidence photo"
                      type="file"
                      onChange={handlePhotoFileChange}
                    />
                  </Button>
                  {stepPhotoPreview && (
                    <Box sx={{ mt: 1, position: 'relative' }}>
                      <img
                        src={stepPhotoPreview}
                        alt="Preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          borderRadius: 4,
                          border: '1px solid #ccc',
                        }}
                      />
                      <IconButton
                        aria-label="Remove uploaded photo"
                        size="small"
                        onClick={clearPhotoFile}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {selectedStep.evidenceRequired && !stepPhotoFile && (
                    <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: 4 }}>
                      Please upload a photo
                    </p>
                  )}
                </Box>
              )}

              {/* Signature Canvas Section */}
              {stepEvidenceType === 'signature' && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <span style={{ fontSize: '0.875rem', color: '#666' }}>
                      <GestureIcon
                        fontSize="small"
                        sx={{ verticalAlign: 'middle', mr: 0.5 }}
                      />
                      Draw your signature below
                    </span>
                    <Button
                      variant="secondary"
                      onClick={clearSignature}
                      startIcon={<ClearIcon />}
                    >
                      Clear
                    </Button>
                  </Box>
                  <canvas
                    aria-label="Signature input"
                    ref={(canvas) => {
                      signatureCanvasRef.current = canvas;
                      if (canvas && !hasSignature) {
                        initSignatureCanvas();
                      }
                    }}
                    width={460}
                    height={150}
                    style={{
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      cursor: 'crosshair',
                      touchAction: 'none',
                      width: '100%',
                      height: 150,
                    }}
                    onMouseDown={handleSignatureMouseDown}
                    onMouseMove={handleSignatureMouseMove}
                    onMouseUp={handleSignatureMouseUp}
                    onMouseLeave={handleSignatureMouseUp}
                    onTouchStart={handleSignatureTouchStart}
                    onTouchMove={handleSignatureTouchMove}
                    onTouchEnd={handleSignatureMouseUp}
                  />
                  {selectedStep.evidenceRequired && !hasSignature && (
                    <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: 4 }}>
                      Please provide your signature
                    </p>
                  )}
                </Box>
              )}

              {/* Comment Field - Always Shown */}
              <TextField
                label={stepEvidenceType === 'text' && selectedStep.evidenceRequired ? 'Notes (Required)' : 'Comment (Optional)'}
                fullWidth
                multiline
                rows={3}
                size="small"
                value={stepComment}
                onChange={e => setStepComment(e.target.value)}
                placeholder="Enter any notes or comments..."
                sx={{ mb: 2 }}
              />

              {/* Location Status */}
              {isCapturingLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
                  <CircularProgress size={16} />
                  <span>Capturing location...</span>
                </Box>
              )}
              {capturedLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4caf50' }}>
                  <LocationOnIcon fontSize="small" />
                  <span>Location captured: {capturedLocation.lat.toFixed(5)}, {capturedLocation.lng.toFixed(5)}</span>
                </Box>
              )}
              {locationError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f44336' }}>
                  <LocationOnIcon fontSize="small" />
                  <span>{locationError}</span>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={handleCancelStepModal}>
            Cancel
          </Button>
          <Button variant="primary"
            onClick={handleConfirmStepComplete}
            disabled={!canCompleteStep() || isCapturingLocation}>
            {isCapturingLocation ? 'Capturing Location...' : 'Complete Step'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Priority Change Dialog */}
      <Dialog open={priorityDialogOpen}
        onClose={handleCancelPriorityDialog}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Change Priority</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16 }}>
            Select a new priority for this task. You can only set the priority to the default or higher.
          </p>
          <TextField
            select
            label="New Priority"
            fullWidth
            size="small"
            value={newPriorityId}
            onChange={e => setNewPriorityId(Number(e.target.value))}
          >
            {getAvailablePriorities().map(p => (
              <MenuItem key={p.id}
                value={p.id}>
                {p.name} {p.id === task?.priorityId && '(Current)'}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={handleCancelPriorityDialog}>
            Cancel
          </Button>
          <Button variant="primary"
            onClick={handleConfirmPriorityChange}
            disabled={newPriorityId === task?.priorityId}>
            Change Priority
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen}
        onClose={handleCancelReassignDialog}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Reassign task</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={3}
            marginTop={1}>
            <Grid item
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Assign to role"
                helperText="Select a role to assign this task to."
                value={selectedRoleId}
                onChange={e => {
                  setSelectedRoleId(e.target.value === '' ? '' : Number(e.target.value));
                  if (e.target.value !== '') setSelectedUserId('');
                }}>
                <MenuItem value="">No role selected</MenuItem>
                {availableRoles.map(role => (
                  <MenuItem key={role.id}
                    value={role.id}>
                    {role.name}
                    {role.id === task?.targetRoleId && ' (Current)'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Or assign to specific user"
                helperText="Select a specific user to assign this task to."
                value={selectedUserId}
                onChange={e => {
                  setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value));
                  if (e.target.value !== '') setSelectedRoleId('');
                }}>
                <MenuItem value="">No user selected</MenuItem>
                {availableUsers.map(user => (
                  <MenuItem key={user.id}
                    value={user.id}>
                    {user.name}
                    {user.id === task?.targetUserId && ' (Current)'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleCancelReassignDialog}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!selectedRoleId && !selectedUserId}
            onClick={handleConfirmReassign}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Extend Due Time Dialog */}
      <Dialog
        open={extendDueDialogOpen}
        onClose={handleCancelExtendDueDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon />
            Extend Due Time
          </Box>
        </DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16 }}>
            Extend the due date and time for this task. An audit event will be recorded.
          </p>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="New Due Date"
              type="date"
              fullWidth
              size="small"
              value={newDueDate}
              onChange={e => setNewDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="New Due Time"
              type="time"
              fullWidth
              size="small"
              value={newDueTime}
              onChange={e => setNewDueTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Reason for Extension"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={extendReason}
            onChange={e => setExtendReason(e.target.value)}
            placeholder="Provide a reason for extending the due date..."
          />
          {task?.dueDateUtc && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: 4 }}>Current Due Date</div>
              <div>{formatDateTime(task.dueDateUtc)}</div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleCancelExtendDueDialog}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmExtendDue}
            disabled={!newDueDate || !newDueTime}
          >
            Extend Due Time
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={handleCancelApprovalDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon />
            Review Task
          </Box>
        </DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16 }}>
            Review the completed task and make an approval decision. This action will be recorded in the audit log.
          </p>

          <TextField
            select
            label="Decision"
            fullWidth
            size="small"
            value={approvalDecision}
            onChange={e => setApprovalDecision(e.target.value as 'Compliant' | 'NonCompliant' | '')}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select a decision...</MenuItem>
            <MenuItem value="Compliant">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon
                  fontSize="small"
                  color="success"
                />
                Approve (Compliant)
              </Box>
            </MenuItem>
            <MenuItem value="NonCompliant">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CancelIcon
                  fontSize="small"
                  color="error"
                />
                Reject (Non-Compliant)
              </Box>
            </MenuItem>
          </TextField>

          {approvalDecision === 'NonCompliant' && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                <input
                  aria-label="Send back for rework"
                  type="checkbox"
                  checked={sendBackForRework}
                  onChange={e => setSendBackForRework(e.target.checked)}
                />
                <RefreshIcon fontSize="small" />
                <span>Send back for rework (clear all steps)</span>
              </label>
              <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#666' }}>
                If checked, all completed steps will be cleared and the assignee can reattempt the task.
              </p>
            </Box>
          )}

          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={approvalNotes}
            onChange={e => setApprovalNotes(e.target.value)}
            placeholder="Add any notes about your decision..."
            sx={{ mb: 2 }}
          />

          {task && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 'bold', marginBottom: 8 }}>Task Summary</div>
              <div style={{ fontSize: '0.875rem' }}>
                <div><strong>Title:</strong> {task.title}</div>
                <div><strong>Steps Completed:</strong> {completedStepIds.length} / {task.steps.length}</div>
                <div><strong>Evidence Items:</strong> {task.evidence.length}</div>
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleCancelApprovalDialog}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmApproval}
            disabled={!approvalDecision}
            color={approvalDecision === 'Compliant' ? 'success' : approvalDecision === 'NonCompliant' ? 'error' : 'primary'}
          >
            {approvalDecision === 'Compliant' ? 'Approve Task' : approvalDecision === 'NonCompliant' ? 'Reject Task' : 'Submit Decision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step View Dialog - View step content before completing */}
      <Dialog
        open={stepViewDialogOpen}
        onClose={handleCloseStepViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon />
            Step {viewingStep?.order}: {viewingStep?.instruction}
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingStep && (
            <>
              {/* Step Type Indicator */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                {getStepTypeIcon(viewingStep.stepType)}
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                  {viewingStep.stepType || 'Text'} Step
                </span>
                {viewingStep.evidenceRequired && (
                  <Chip
                    label={`Evidence Required: ${viewingStep.evidenceType || 'Any'}`}
                    size="small"
                    color="warning"
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Box>

              {/* Instruction Text */}
              {viewingStep.instruction && (
                <Box sx={{ mb: 3 }}>
                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: 4 }}>Instructions</div>
                  <div style={{ fontSize: '1rem', lineHeight: 1.6 }}>{viewingStep.instruction}</div>
                </Box>
              )}

              {/* Linked Document */}
              {viewingStep.stepType === 'document' && viewingStep.linkedDocumentId && (
                <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DescriptionIcon color="primary" />
                    <span style={{ fontWeight: 'bold' }}>Linked Document</span>
                  </Box>
                  <div style={{ fontSize: '1rem' }}>{viewingStep.linkedDocumentName || `Document #${viewingStep.linkedDocumentId}`}</div>
                  <Button
                    variant="secondary"
                    startIcon={<VisibilityIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => {
                      // In real implementation, navigate to document viewer or open in new tab
                      window.open(`/documents/${viewingStep.linkedDocumentId}`, '_blank');
                    }}
                  >
                    View Document
                  </Button>
                </Box>
              )}

              {/* Linked URL */}
              {viewingStep.stepType === 'url' && viewingStep.linkedUrl && (
                <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LinkIcon color="primary" />
                    <span style={{ fontWeight: 'bold' }}>External Link</span>
                  </Box>
                  <div style={{ fontSize: '1rem' }}>
                    {viewingStep.linkedUrlLabel || viewingStep.linkedUrl}
                  </div>
                  <a
                    href={viewingStep.linkedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none', display: 'block', marginTop: 8 }}
                  >
                    {viewingStep.linkedUrl}
                  </a>
                  <Button
                    variant="secondary"
                    startIcon={<LinkIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => {
                      window.open(viewingStep.linkedUrl!, '_blank');
                    }}
                  >
                    Open Link
                  </Button>
                </Box>
              )}

              {/* Step Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <span style={{ color: '#666' }}>Status:</span>
                {completedStepIds.includes(viewingStep.id) ? (
                  <Chip
                    label="Completed"
                    size="small"
                    color="success"
                  />
                ) : (
                  <Chip
                    label="Pending"
                    size="small"
                    color="default"
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleCloseStepViewDialog}
          >
            Close
          </Button>
          {viewingStep && !completedStepIds.includes(viewingStep.id) && canUpdateProgress && isTaskIncomplete && (
            <Button
              variant="primary"
              onClick={handleViewAndComplete}
              startIcon={<CheckCircleIcon />}
            >
              Complete This Step
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TaskDetails;
